import { LoadingOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { FC, useEffect, useState } from 'react';
import { CloseIcon } from '../../../../../assets/images/bussines';
import { SwitchText, SwitchStatus } from '../SwitchText/SwitchText';
import { NudosTextInput } from '../../../../../components/NudosComponents';
import { ID_PAYMENT_AGREEMENT, TYPE_PAYMENT_AGREEMENT } from '../../../../../types/bussines';
import { IDetailCard } from '../CardBussines';
import './ModalActivateCredit.scss';

interface IActivateCredit {
  handleActivatePaymentAgreement: (type: TYPE_PAYMENT_AGREEMENT, days: string | number) => void;
  closeModal: () => void;
  loadingActivate: boolean;
  organizationName?: string;
  isEditPaymentAgreement?: boolean;
  handleDeactivateAgreementPayment?: (value: boolean, type: TYPE_PAYMENT_AGREEMENT) => void;
  onCancel?: () => void;
  dataBusiness?: IDetailCard[];
  organizationId: number;
}

const ModalActivateCredit: FC<IActivateCredit> = ({
  handleActivatePaymentAgreement,
  closeModal,
  loadingActivate,
  organizationName = 'N/A',
  isEditPaymentAgreement,
  handleDeactivateAgreementPayment,
  onCancel,
  dataBusiness,
  organizationId
}: IActivateCredit) => {
  const [creditDaysForOrder, setCreditDaysForOrder] = useState<number>(0);
  const [creditDaysForService, setCreditDaysForService] = useState<number>(0);
  const [activeOrder, setActiveOrder] = useState<boolean>(false);
  const [activeService, setActiveService] = useState<boolean>(false);
  const [isConfirmUpdate, setIsConfirmUpdate] = useState<boolean>(false);

  const onChangeValueOrder = (value: number | string) => {
    setCreditDaysForOrder(Number(value));
  };

  const onChangeValueService = (value: number | string) => {
    setCreditDaysForService(Number(value));
  };

  const selectedCard = dataBusiness?.find(item => item.id === organizationId);

  const creditForOrderIndex = selectedCard?.organizationCommercialPreferences?.findIndex(
    (item: any) => item?.commercialPreferenceTypeId === ID_PAYMENT_AGREEMENT.REQUIRE_PAYMENT_AGREEMENT_FOR_ORDER
  );

  const creditForOrderValue =
    creditForOrderIndex !== undefined && creditForOrderIndex !== -1
      ? selectedCard?.organizationCommercialPreferences?.[creditForOrderIndex]?.commercialPreferenceValue
      : undefined;

  const creditForServiceIndex = selectedCard?.organizationCommercialPreferences?.findIndex(
    (item: any) => item?.commercialPreferenceTypeId === ID_PAYMENT_AGREEMENT.REQUIRE_PAYMENT_AGREEMENT_FOR_SERVICE
  );
  const creditForServiceValue =
    creditForServiceIndex !== undefined && creditForServiceIndex !== -1
      ? selectedCard?.organizationCommercialPreferences?.[creditForServiceIndex]?.commercialPreferenceValue
      : undefined;
  const deadlineOrder: number = creditForOrderValue || 0;
  const deadlineService: number = creditForServiceValue || 0;

  const activateDays = () => {
    const currentCreditDaysForOrder = deadlineOrder;
    const currentCreditDaysForService = deadlineService;

    const hasOrderChanged = creditDaysForOrder !== currentCreditDaysForOrder;
    const hasServiceChanged = creditDaysForService !== currentCreditDaysForService;

    if ((hasOrderChanged || hasServiceChanged) && handleActivatePaymentAgreement) {
      if (hasOrderChanged && hasServiceChanged) {
        handleActivatePaymentAgreement(TYPE_PAYMENT_AGREEMENT.ORDER, creditDaysForOrder);
        handleActivatePaymentAgreement(TYPE_PAYMENT_AGREEMENT.SERVICE, creditDaysForService);
      } else if (hasOrderChanged) {
        handleActivatePaymentAgreement(TYPE_PAYMENT_AGREEMENT.ORDER, creditDaysForOrder);
      } else if (hasServiceChanged) {
        handleActivatePaymentAgreement(TYPE_PAYMENT_AGREEMENT.SERVICE, creditDaysForService);
      }
    }
  };

  const handleActiveOrder = (e: React.ChangeEvent<HTMLInputElement> | boolean) => {
    const isEnabled = typeof e === 'boolean' ? e : e?.target?.checked ?? !activeOrder;
    if (handleDeactivateAgreementPayment) {
      handleDeactivateAgreementPayment(!isEnabled, TYPE_PAYMENT_AGREEMENT.ORDER);
    }
    setActiveOrder(isEnabled);
  };

  const handleActiveService = (e: React.ChangeEvent<HTMLInputElement> | boolean) => {
    const isEnabled = typeof e === 'boolean' ? e : e?.target?.checked ?? !activeOrder;
    if (handleDeactivateAgreementPayment) {
      handleDeactivateAgreementPayment(!isEnabled, TYPE_PAYMENT_AGREEMENT.SERVICE);
    }
    setActiveService(isEnabled);
  };

  const handleConfirmUpdate = () => {
    setIsConfirmUpdate(true);
  };

  const quantityDays = {
    min: 0,
    max: 60
  };

  const titleModal = isEditPaymentAgreement ? 'Actualizar acuerdo de pago' : 'Acuerdo de pago';
  const creditDaysForOrderError = creditDaysForOrder > quantityDays.max;
  const creditDaysForServiceError = creditDaysForService > quantityDays.max;
  const creditDaysForOrderIsEqualCero = creditDaysForOrder === quantityDays.min;
  const creditDaysForServiceIsEqualCero = creditDaysForService === quantityDays.min;

  const getCreditDaysErrorMsg = (hasError: boolean): string =>
    hasError
      ? 'El plazo mínimo es de 1 día y el máximo es de 60 días.'
      : 'Ingresa el plazo en un rango no mayor a 60 días';

  const creditDaysForOrderErrorMsg = getCreditDaysErrorMsg(creditDaysForOrderError);
  const creditDaysForServiceErrorMsg = getCreditDaysErrorMsg(creditDaysForServiceError);

  const hasOrderChanged = creditDaysForOrder !== deadlineOrder && creditDaysForOrder > 0;
  const hasServiceChanged = creditDaysForService !== deadlineService && creditDaysForService > 0;
  const canActivateButton =
    (hasOrderChanged || hasServiceChanged) && !creditDaysForOrderError && !creditDaysForServiceError;

  const disableIfSwitchIsActive = () => {
    const orderBlocked = activeOrder && creditDaysForOrderIsEqualCero;
    const serviceBlocked = activeService && creditDaysForServiceIsEqualCero;
    return orderBlocked || serviceBlocked;
  };

  const updateCreditDaysAndStatus = () => {
    if (deadlineOrder >= 1) {
      setCreditDaysForOrder(deadlineOrder);
      setActiveOrder(true);
    } else {
      setActiveOrder(false);
      setCreditDaysForOrder(0);
    }
    if (deadlineService >= 1) {
      setCreditDaysForService(deadlineService);
      setActiveService(true);
    } else {
      setActiveService(false);
      setCreditDaysForService(0);
    }
  };

  useEffect(() => {
    updateCreditDaysAndStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadlineOrder, deadlineService]);

  return (
    <div id="modalActivateCredit">
      <div className="bgModal" onClick={closeModal}></div>
      <div className="containerModal">
        <CloseIcon className="positionClose" onClick={closeModal} />
        <div className="containerTitle">{titleModal}</div>
        <div className="containerSubtitle">
          El nuevo plazo no afectará órdenes y servicios realizados previamente para Empresa: {organizationName}
        </div>

        {!isConfirmUpdate ? (
          <div className="containerSwitches">
            <div className="cardSwitch">
              <div className="title">Órdenes</div>

              <div id="bussinesCustomSwitch">
                <SwitchText
                  status={activeOrder ? SwitchStatus.ACTIVE : SwitchStatus.INACTIVE}
                  handleChangeStatus={(e: React.ChangeEvent<HTMLInputElement> | boolean) => {
                    handleActiveOrder(e);
                  }}
                />
              </div>
            </div>
            {activeOrder && (
              <div className="containerInput">
                <div className="boxInput">
                  <div
                    className={`label ${!creditDaysForOrderIsEqualCero ? 'active' : 'inactive'} ${
                      creditDaysForOrderError ? 'error' : ''
                    }`}
                  >
                    Plazo de acuerdo de pago
                  </div>
                  <>
                    <NudosTextInput
                      customClassName={`inputDays ${creditDaysForOrderError ? 'inputDaysError' : ''}`}
                      isFilled={creditDaysForOrder > 0}
                      defaultValue={creditDaysForOrder > 0 ? creditDaysForOrder.toString() : ''}
                      isNumericInput
                      handleChange={(value: string) => {
                        onChangeValueOrder(value);
                      }}
                      placeholder="Escribe el plazo"
                    />
                  </>
                  <div className={`message ${creditDaysForOrderError ? 'msgError' : ''}`}>
                    {creditDaysForOrderErrorMsg}
                  </div>
                </div>
              </div>
            )}
            <div className="cardSwitch services">
              <div className="title">Servicios</div>

              <div id="bussinesCustomSwitch">
                <SwitchText
                  status={activeService ? SwitchStatus.ACTIVE : SwitchStatus.INACTIVE}
                  handleChangeStatus={(e: React.ChangeEvent<HTMLInputElement> | boolean) => {
                    handleActiveService(e);
                  }}
                />
              </div>
            </div>
            {activeService && (
              <div className="containerInput">
                <div className="boxInput">
                  <div
                    className={`label ${!creditDaysForServiceIsEqualCero ? 'active' : 'inactive'} ${
                      creditDaysForServiceError ? 'error' : ''
                    }`}
                  >
                    Plazo de acuerdo de pago
                  </div>
                  <>
                    <NudosTextInput
                      customClassName={`inputDays ${creditDaysForServiceError ? 'inputDaysError' : ''}`}
                      isFilled={creditDaysForService > 0}
                      defaultValue={creditDaysForService > 0 ? creditDaysForService.toString() : ''}
                      isNumericInput
                      handleChange={(value: string) => {
                        onChangeValueService(value);
                      }}
                      placeholder="Escribe el plazo"
                    />
                  </>
                  <div className={`message ${creditDaysForServiceError ? 'msgError' : ''}`}>
                    {creditDaysForServiceErrorMsg}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="containerConfirmUpdate">
            <div className="infoUpdate">
              <p className="titleInfoUpdate">Órdenes</p>
              <div className="dealing">
                <p className="dealingTitle">Plazo anterior:</p>
                <p className="dealingValue">{deadlineOrder} días</p>
              </div>
              <div className="dealing">
                <p className="dealingTitle">Plazo actualizado:</p>
                <p className="dealingValue">{creditDaysForOrder} días</p>
              </div>
            </div>
            <div className="infoUpdate">
              <p className="titleInfoUpdate">Servicios</p>
              <div className="dealing">
                <p className="dealingTitle">Plazo anterior:</p>
                <p className="dealingValue">{deadlineService} días</p>
              </div>
              <div className="dealing">
                <p className="dealingTitle">Plazo actualizado:</p>
                <p className="dealingValue">{creditDaysForService} días</p>
              </div>
            </div>
          </div>
        )}
        {!isConfirmUpdate ? (
          <div className="containerButton">
            <Button
              disabled={loadingActivate || !canActivateButton || disableIfSwitchIsActive()}
              className="creditButtton"
              onClick={isEditPaymentAgreement ? handleConfirmUpdate : activateDays}
            >
              {loadingActivate ? <LoadingOutlined style={{ fontSize: '20px' }} /> : 'Guardar'}
            </Button>
          </div>
        ) : (
          <div className="containerInfoUpdateButtons">
            <div className="titleInfoUpdate">¿Seguro que deseas continuar?</div>
            <div className="buttons">
              <Button
                disabled={!creditDaysForOrder || loadingActivate || creditDaysForOrderError}
                className="cancelButton"
                onClick={onCancel}
              >
                {loadingActivate ? <LoadingOutlined style={{ fontSize: '20px' }} /> : 'Cancelar'}
              </Button>
              <Button disabled={loadingActivate || !canActivateButton} className="updateButton" onClick={activateDays}>
                {loadingActivate ? <LoadingOutlined style={{ fontSize: '20px' }} /> : 'Actualizar acuerdo de pago'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalActivateCredit;
